import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Clipboard,
  FileText,
  Loader2,
  MessageSquarePlus,
  Paperclip,
  PauseCircle,
  Search,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import ComplaintRecoverySettings from "@/components/admin_dashboard/ComplaintRecoverySettings";

type CaseSeverity = "emergency" | "high" | "normal" | "low";
type CaseStatus = "open" | "acknowledged" | "in_progress" | "waiting_customer" | "resolved" | "closed";

interface CrisisCase {
  id: string;
  reference: string;
  business_id: string;
  contact_id: string | null;
  conversation_id: string | null;
  booking_id: string | null;
  complaint_id: string | null;
  outlet_id: string | null;
  listing_id: string | null;
  resource_id: string | null;
  customer_label: string | null;
  customer_channel: string | null;
  case_type: "customer_case" | "operational_incident";
  category: string;
  severity: CaseSeverity;
  status: CaseStatus;
  source: string;
  title: string;
  summary: string;
  assigned_role: string | null;
  assigned_user_id: string | null;
  acknowledgement_due_at: string;
  resolution_due_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
  resolution_summary: string | null;
  feedback_request_id: string | null;
  customer_outcome: "recovered" | "not_recovered" | "unknown" | null;
  outreach_pause_recommended: boolean;
  created_at: string;
  updated_at: string;
}

interface CaseEvent {
  id: string;
  event_type: string;
  body: string | null;
  actor_type: string;
  actor_user_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

interface CaseFile {
  id: string;
  storage_path: string;
  file_name: string;
  mime_type: string | null;
  size_bytes: number;
  created_at: string;
}

interface StaffOption {
  user_id: string;
  display_name: string | null;
  email: string;
}

interface ContactOption {
  contact_id: string;
  display_name: string | null;
  primary_email: string | null;
  primary_phone: string | null;
}

interface CustomerFeedback {
  id: string;
  rating: number | null;
  comment: string | null;
  status: "pending" | "submitted" | "expired" | "cancelled";
  sent_at: string | null;
  submitted_at: string | null;
  recovery_required: boolean;
  recovery_status: "not_required" | "pending" | "processing" | "opened" | "failed";
  recovery_case_id: string | null;
  created_at: string;
}

interface ComplaintRecord {
  id: string;
  source: string;
  rating: number | null;
  reviewer_name: string | null;
  reviewer_is_anonymous: boolean;
  customer_name: string | null;
  customer_email: string | null;
  contact_id: string | null;
  identity_status: string;
  subject: string | null;
  body: string;
  status: string;
  public_response: string | null;
  private_response: string | null;
  recovery_claimed_at: string | null;
  recovery_token_expires_at: string | null;
  received_at: string;
}

interface RecoveryOffer {
  id: string;
  offer_type: "fixed_credit" | "percentage_discount";
  value: number;
  currency: string;
  label: string;
  status: string;
  code_hint: string;
  expires_at: string;
  issued_at: string | null;
  redeemed_at: string | null;
}

const CATEGORY_LABELS: Record<string, string> = {
  clinical: "Clinical",
  safety: "Safety",
  service: "Service",
  billing: "Billing",
  privacy: "Privacy",
  booking: "Booking",
  operational: "Operational",
  other: "Other",
};

const ROLE_LABELS: Record<string, string> = {
  owner: "Owner",
  clinic_manager: "Clinic manager",
  clinician: "Clinician",
  front_desk: "Front desk",
  billing: "Billing",
  operations: "Operations",
  outlet_manager: "Outlet manager",
  security: "Security",
};

const STATUS_LABELS: Record<CaseStatus, string> = {
  open: "Open",
  acknowledged: "Acknowledged",
  in_progress: "In progress",
  waiting_customer: "Waiting for customer",
  resolved: "Resolved",
  closed: "Closed",
};

const severityClass: Record<CaseSeverity, string> = {
  emergency: "border-red-300 bg-red-50 text-red-700",
  high: "border-orange-300 bg-orange-50 text-orange-700",
  normal: "border-blue-200 bg-blue-50 text-blue-700",
  low: "border-border bg-muted text-muted-foreground",
};

const fmt = (iso: string) => new Date(iso).toLocaleString(undefined, {
  day: "numeric",
  month: "short",
  hour: "numeric",
  minute: "2-digit",
});

const relativeDeadline = (iso: string) => {
  const ms = new Date(iso).getTime() - Date.now();
  const overdue = ms < 0;
  const abs = Math.abs(ms);
  const minutes = Math.max(1, Math.round(abs / 60_000));
  const text = minutes < 60
    ? `${minutes}m`
    : minutes < 24 * 60
      ? `${Math.round(minutes / 60)}h`
      : `${Math.round(minutes / (24 * 60))}d`;
  return overdue ? `${text} overdue` : `in ${text}`;
};

const contactLabel = (contact: ContactOption) =>
  contact.display_name || contact.primary_email || contact.primary_phone || `Contact ${contact.contact_id.slice(0, 8)}`;

const sanitizeName = (name: string) => name.replace(/[^a-zA-Z0-9._-]/g, "_");

export default function CrisisInbox() {
  const { businessId, user, isStaff } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [busy, setBusy] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolution, setResolution] = useState("");
  const [customerOutcome, setCustomerOutcome] = useState("");
  const [comment, setComment] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [linkContactSearch, setLinkContactSearch] = useState("");
  const [pickedContact, setPickedContact] = useState<ContactOption | null>(null);
  const [form, setForm] = useState({
    summary: "",
    category: "service",
    severity: "normal",
    assigned_role: "owner",
    case_type: "customer_case",
    pause_outreach: true,
  });
  const fileInput = useRef<HTMLInputElement>(null);

  const selectedId = searchParams.get("case");

  const { data: cases = [], isLoading, refetch } = useQuery({
    queryKey: ["crisis-cases", businessId],
    queryFn: async (): Promise<CrisisCase[]> => {
      const { data, error } = await (supabase as any)
        .from("crisis_cases")
        .select("*")
        .eq("business_id", businessId)
        .order("created_at", { ascending: false })
        .limit(250);
      if (error) throw new Error(error.message);
      return (data ?? []) as CrisisCase[];
    },
    enabled: !!businessId,
    refetchInterval: 60_000,
  });

  const { data: feedback = [] } = useQuery({
    queryKey: ["customer-feedback-metrics", businessId],
    queryFn: async (): Promise<CustomerFeedback[]> => {
      const since = new Date(Date.now() - 30 * 86_400_000).toISOString();
      const { data, error } = await (supabase as any)
        .from("customer_feedback_requests")
        .select("id, rating, comment, status, sent_at, submitted_at, recovery_required, recovery_status, recovery_case_id, created_at")
        .eq("business_id", businessId)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw new Error(error.message);
      return (data ?? []) as CustomerFeedback[];
    },
    enabled: !!businessId,
    refetchInterval: 60_000,
  });

  const selected = cases.find((row) => row.id === selectedId) ?? null;

  const { data: complaint = null } = useQuery({
    queryKey: ["crisis-complaint", selectedId],
    queryFn: async (): Promise<ComplaintRecord | null> => {
      const { data, error } = await (supabase as any).from("customer_complaints")
        .select("id, source, rating, reviewer_name, reviewer_is_anonymous, customer_name, customer_email, contact_id, identity_status, subject, body, status, public_response, private_response, recovery_claimed_at, recovery_token_expires_at, received_at")
        .eq("case_id", selectedId).maybeSingle();
      if (error) throw new Error(error.message);
      return data as ComplaintRecord | null;
    },
    enabled: !!selectedId,
  });

  const { data: recoveryOffer = null } = useQuery({
    queryKey: ["crisis-recovery-offer", complaint?.id],
    queryFn: async (): Promise<RecoveryOffer | null> => {
      const { data, error } = await (supabase as any).from("complaint_recovery_offers")
        .select("id, offer_type, value, currency, label, status, code_hint, expires_at, issued_at, redeemed_at")
        .eq("complaint_id", complaint?.id).maybeSingle();
      if (error) throw new Error(error.message);
      return data as RecoveryOffer | null;
    },
    enabled: !!complaint?.id,
  });

  const { data: events = [] } = useQuery({
    queryKey: ["crisis-case-events", selectedId],
    queryFn: async (): Promise<CaseEvent[]> => {
      const { data, error } = await (supabase as any)
        .from("crisis_case_events")
        .select("id, event_type, body, actor_type, actor_user_id, metadata, created_at")
        .eq("case_id", selectedId)
        .order("created_at", { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as CaseEvent[];
    },
    enabled: !!selectedId,
  });

  const { data: files = [] } = useQuery({
    queryKey: ["crisis-case-files", selectedId],
    queryFn: async (): Promise<CaseFile[]> => {
      const { data, error } = await (supabase as any)
        .from("crisis_case_files")
        .select("id, storage_path, file_name, mime_type, size_bytes, created_at")
        .eq("case_id", selectedId)
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as CaseFile[];
    },
    enabled: !!selectedId,
  });

  const { data: staff = [] } = useQuery({
    queryKey: ["crisis-staff", businessId],
    queryFn: async (): Promise<StaffOption[]> => {
      const { data } = await (supabase as any)
        .from("staff_members")
        .select("user_id, display_name, email")
        .eq("business_id", businessId)
        .eq("status", "active")
        .not("user_id", "is", null);
      return (data ?? []) as StaffOption[];
    },
    enabled: !!businessId,
  });

  const { data: contacts = [] } = useQuery({
    queryKey: ["crisis-contact-search", businessId, contactSearch],
    queryFn: async (): Promise<ContactOption[]> => {
      // PostgREST `.or()` uses commas and parentheses as syntax. Strip them
      // from the search fragment so a customer name cannot alter the filter.
      const term = contactSearch.trim().replace(/[,%()]/g, " ");
      const { data, error } = await (supabase as any)
        .from("contacts")
        .select("contact_id, display_name, primary_email, primary_phone")
        .eq("business_id", businessId)
        .or(`display_name.ilike.%${term}%,primary_email.ilike.%${term}%,primary_phone.ilike.%${term}%`)
        .limit(8);
      if (error) throw new Error(error.message);
      return (data ?? []) as ContactOption[];
    },
    enabled: createOpen && contactSearch.trim().length >= 2 && !pickedContact,
  });

  const { data: linkContacts = [] } = useQuery({
    queryKey: ["crisis-link-contact-search", businessId, linkContactSearch],
    queryFn: async (): Promise<ContactOption[]> => {
      const term = linkContactSearch.trim().replace(/[,%()]/g, " ");
      const { data, error } = await (supabase as any).from("contacts")
        .select("contact_id, display_name, primary_email, primary_phone")
        .eq("business_id", businessId)
        .or(`display_name.ilike.%${term}%,primary_email.ilike.%${term}%,primary_phone.ilike.%${term}%`)
        .limit(8);
      if (error) throw new Error(error.message);
      return (data ?? []) as ContactOption[];
    },
    enabled: !!complaint && !isStaff && linkContactSearch.trim().length >= 2,
  });

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["crisis-cases", businessId] }),
      queryClient.invalidateQueries({ queryKey: ["crisis-case-events", selectedId] }),
      queryClient.invalidateQueries({ queryKey: ["crisis-case-files", selectedId] }),
      queryClient.invalidateQueries({ queryKey: ["outreach-holds", businessId] }),
      queryClient.invalidateQueries({ queryKey: ["customer-feedback-metrics", businessId] }),
      queryClient.invalidateQueries({ queryKey: ["crisis-complaint", selectedId] }),
      queryClient.invalidateQueries({ queryKey: ["crisis-recovery-offer", complaint?.id] }),
      queryClient.invalidateQueries({ queryKey: ["complaint-connections", businessId] }),
    ]);
  };

  const complaintInvoke = async (action: string, payload: Record<string, unknown> = {}) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const { data, error } = await supabase.functions.invoke("complaint-connections", {
      body: { action, ...payload },
      headers: sessionData.session ? { Authorization: `Bearer ${sessionData.session.access_token}` } : undefined,
    });
    if (error) {
      try {
        const payload = await (error as { context?: Response }).context?.json();
        throw new Error(String(payload?.error ?? error.message).replace(/_/g, " "));
      } catch (parsed) {
        throw parsed instanceof Error ? parsed : error;
      }
    }
    if (data?.error) throw new Error(String(data.error).replace(/_/g, " "));
    return data;
  };

  const linkComplaintContact = async (contact: ContactOption) => {
    if (!complaint) return;
    setBusy("link-contact");
    try {
      await complaintInvoke("link_complaint_contact", { complaint_id: complaint.id, contact_id: contact.contact_id });
      setLinkContactSearch("");
      toast.success("Customer verified and linked to this complaint.");
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Customer could not be linked");
    } finally { setBusy(null); }
  };

  const copyRecoveryLink = async () => {
    if (!complaint) return;
    setBusy("recovery-link");
    try {
      const data = await complaintInvoke("get_recovery_link", { complaint_id: complaint.id });
      await navigator.clipboard.writeText(String(data.recovery_url));
      toast.success("Private recovery link copied. Share it privately, never in a public review reply.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Recovery link unavailable");
    } finally { setBusy(null); }
  };

  const proposeOffer = async () => {
    if (!complaint) return;
    setBusy("propose-offer");
    try {
      await complaintInvoke("propose_offer", { complaint_id: complaint.id });
      toast.success("Recovery offer sent to the approval queue. Nothing has been sent to the customer yet.");
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Offer is not eligible");
    } finally { setBusy(null); }
  };

  const invoke = async (action: string, payload: Record<string, unknown> = {}) => {
    if (!businessId) throw new Error("No business selected");
    const { data: sessionData } = await supabase.auth.getSession();
    const { data, error } = await supabase.functions.invoke("case-management", {
      body: { action, business_id: businessId, ...payload },
      headers: sessionData.session
        ? { Authorization: `Bearer ${sessionData.session.access_token}` }
        : undefined,
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  };

  const act = async (key: string, action: string, payload: Record<string, unknown> = {}, success?: string) => {
    if (!selected) return;
    setBusy(key);
    try {
      await invoke(action, { case_id: selected.id, ...payload });
      if (success) toast.success(success);
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Case update failed");
    } finally {
      setBusy(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cases.filter((row) => {
      if (statusFilter === "active" && ["resolved", "closed"].includes(row.status)) return false;
      if (statusFilter === "resolved" && !["resolved", "closed"].includes(row.status)) return false;
      if (severityFilter !== "all" && row.severity !== severityFilter) return false;
      if (q && !`${row.reference} ${row.title} ${row.summary} ${row.customer_label ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [cases, search, severityFilter, statusFilter]);

  const metrics = useMemo(() => {
    const active = cases.filter((row) => !["resolved", "closed"].includes(row.status));
    const now = Date.now();
    return {
      active: active.length,
      urgent: active.filter((row) => row.severity === "emergency" || row.severity === "high").length,
      overdue: active.filter((row) =>
        (!row.acknowledged_at && new Date(row.acknowledgement_due_at).getTime() < now) ||
        new Date(row.resolution_due_at).getTime() < now
      ).length,
      unassigned: active.filter((row) => !row.assigned_role && !row.assigned_user_id).length,
    };
  }, [cases]);

  const feedbackMetrics = useMemo(() => {
    const sent = feedback.filter((row) => !!row.sent_at && row.status !== "cancelled");
    const responses = feedback.filter((row) => row.status === "submitted" && row.rating != null);
    const caseById = new Map(cases.map((row) => [row.id, row] as const));
    const recoveryCases = feedback
      .filter((row) => row.recovery_required)
      .map((row) => row.recovery_case_id ? caseById.get(row.recovery_case_id) : undefined);
    const recovered = recoveryCases.filter((row) => row?.customer_outcome === "recovered").length;
    const notRecovered = recoveryCases.filter((row) => row?.customer_outcome === "not_recovered").length;
    const needsRecovery = feedback.filter((row) => row.recovery_required).length;
    return {
      responseRate: sent.length ? Math.round((responses.length / sent.length) * 100) : null,
      average: responses.length
        ? (responses.reduce((sum, row) => sum + Number(row.rating), 0) / responses.length).toFixed(1)
        : null,
      needsRecovery,
      recovered,
      notRecovered,
      // Unknown and still-open outcomes remain in the denominator. This keeps
      // the headline honest instead of reporting 100% from one recovered case
      // while nine others still have no confirmed customer outcome.
      confirmedRate: needsRecovery ? Math.round((recovered / needsRecovery) * 100) : null,
    };
  }, [cases, feedback]);

  const selectCase = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("case", id);
    else next.delete("case");
    setSearchParams(next, { replace: true });
  };

  const createCase = async () => {
    if (!form.summary.trim()) return toast.error("Add a factual case summary.");
    setBusy("create");
    try {
      const data = await invoke("create", {
        ...form,
        summary: form.summary.trim(),
        contact_id: pickedContact?.contact_id ?? null,
        customer_label: pickedContact ? contactLabel(pickedContact) : null,
      });
      setCreateOpen(false);
      setForm({ summary: "", category: "service", severity: "normal", assigned_role: "owner", case_type: "customer_case", pause_outreach: true });
      setPickedContact(null);
      setContactSearch("");
      await refresh();
      const id = data?.case?.id as string | undefined;
      if (id) selectCase(id);
      toast.success(data?.created === false ? "The existing live case was updated." : "Case created and routed.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Couldn't create case");
    } finally {
      setBusy(null);
    }
  };

  const addComment = async () => {
    if (!comment.trim() || !selected) return;
    await act("comment", "comment", { comment: comment.trim() }, "Internal note added.");
    setComment("");
  };

  const uploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selected || !businessId) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Files must be 10MB or smaller.");
      return;
    }
    setBusy("file");
    const path = `${businessId}/${selected.id}/${Date.now()}-${sanitizeName(file.name)}`;
    try {
      const { error } = await supabase.storage.from("crisis-case-files").upload(path, file);
      if (error) throw error;
      try {
        await invoke("attach_file", {
          case_id: selected.id,
          storage_path: path,
          file_name: file.name,
          mime_type: file.type || null,
          size_bytes: file.size,
        });
      } catch (metadataError) {
        await supabase.storage.from("crisis-case-files").remove([path]);
        throw metadataError;
      }
      toast.success("File attached to the private case timeline.");
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Upload failed");
    } finally {
      setBusy(null);
      event.target.value = "";
    }
  };

  const openFile = async (file: CaseFile) => {
    const { data, error } = await supabase.storage.from("crisis-case-files").createSignedUrl(file.storage_path, 60);
    if (error || !data?.signedUrl) return toast.error("Couldn't open this file.");
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };

  const resolveCase = async () => {
    if (!resolution.trim()) return toast.error("Record what was done before resolving the case.");
    if (!customerOutcome) return toast.error("Record whether the customer was recovered, not recovered, or not yet known.");
    await act("resolve", "resolve", {
      resolution_summary: resolution.trim(),
      customer_outcome: customerOutcome,
    }, "Case resolved and its outreach pause cleared.");
    setResolveOpen(false);
    setResolution("");
    setCustomerOutcome("");
  };

  if (!businessId) return <p className="text-sm text-muted-foreground">Select a business to view cases.</p>;

  const requestedView = searchParams.get("view");
  const activeView = requestedView === "insights"
    ? "insights"
    : requestedView === "settings" && !isStaff
      ? "settings"
      : "inbox";
  const changeView = (view: "inbox" | "insights" | "settings") => {
    const next = new URLSearchParams(searchParams);
    if (view === "inbox") next.delete("view");
    else next.set("view", view);
    next.delete("case");
    setSearchParams(next, { replace: true });
  };
  const summaryMetrics = [
    { label: "Active", value: metrics.active, emphasis: false },
    { label: "High priority", value: metrics.urgent, emphasis: metrics.urgent > 0 },
    { label: "Overdue", value: metrics.overdue, emphasis: metrics.overdue > 0 },
    { label: "Unassigned", value: metrics.unassigned, emphasis: false },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-heading font-semibold">Recovery inbox</h2>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Own sensitive customer issues, keep response deadlines visible and record how each case ended.
          </p>
        </div>
        {activeView === "inbox" && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>Refresh</Button>
            {!isStaff && <Button size="sm" onClick={() => setCreateOpen(true)}>Open case</Button>}
          </div>
        )}
      </div>

      <div className="flex border-b border-border" role="tablist" aria-label="Recovery workspace">
        {([
          ["inbox", "Inbox"],
          ["insights", "Insights"],
          ...(!isStaff ? [["settings", "Settings"]] : []),
        ] as Array<["inbox" | "insights" | "settings", string]>).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={activeView === value}
            onClick={() => changeView(value)}
            className={cn(
              "-mb-px border-b-2 px-4 py-2.5 text-sm transition-colors",
              activeView === value
                ? "border-foreground font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {activeView === "inbox" && (
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3 border-b border-border pb-4">
          {summaryMetrics.map(({ label, value, emphasis }) => (
            <div key={label} className="flex items-baseline gap-2">
              <span className={cn("text-lg font-semibold", emphasis && "text-orange-600")}>{value}</span>
              <span className="text-xs text-muted-foreground">{label}</span>
            </div>
          ))}
        </div>
      )}

      {activeView === "insights" && <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold">Private feedback · last 30 days</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              A resolved case is not counted as recovery unless a person records the customer outcome.
            </p>
          </div>
        </div>
        <div className="mt-5 grid gap-x-6 gap-y-4 border-y border-border py-4 sm:grid-cols-2 xl:grid-cols-5">
          {[
            ["Response rate", feedbackMetrics.responseRate == null ? "—" : `${feedbackMetrics.responseRate}%`],
            ["Average rating", feedbackMetrics.average == null ? "—" : `${feedbackMetrics.average} / 5`],
            ["Needed recovery", String(feedbackMetrics.needsRecovery)],
            ["Recovery confirmed", feedbackMetrics.confirmedRate == null ? "—" : `${feedbackMetrics.confirmedRate}%`],
            ["Not recovered", String(feedbackMetrics.notRecovered)],
          ].map(([label, value]) => (
            <div key={label}>
              <p className="text-[11px] text-muted-foreground">{label}</p>
              <p className="mt-1 text-xl font-semibold">{value}</p>
            </div>
          ))}
        </div>
        {feedback.some((row) => row.status === "submitted") && (
          <div className="mt-4 border-t border-border pt-4">
            <p className="mb-2 text-xs font-medium">Recent private responses</p>
            <div className="grid gap-2 lg:grid-cols-2">
              {feedback.filter((row) => row.status === "submitted").slice(0, 6).map((row) => (
                <div key={row.id} className="rounded-lg border border-border px-3 py-2.5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold">{row.rating} / 5</p>
                    <div className="flex items-center gap-2">
                      <Badge variant={row.recovery_required ? "destructive" : "secondary"} className="text-[10px]">
                        {row.recovery_required
                          ? row.recovery_status === "opened" ? "Human recovery case opened" : "Recovery handoff pending"
                          : "No recovery case needed"}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">{fmt(row.submitted_at ?? row.created_at)}</span>
                    </div>
                  </div>
                  <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
                    {row.comment || "No written comment."}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="mt-4 text-[11px] leading-relaxed text-muted-foreground">
          Private feedback is used to spot service issues and open recovery work. It is never used to filter who may leave a public review.
        </p>
      </div>}

      {activeView === "settings" && !isStaff && <ComplaintRecoverySettings />}

      {activeView === "inbox" && <div className="grid min-h-[620px] overflow-hidden rounded-lg border border-border bg-card lg:grid-cols-[390px_1fr]">
        <div className={cn("border-r border-border", selected ? "hidden lg:block" : "block")}>
          <div className="space-y-3 border-b border-border p-3">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search case or customer…" className="h-9 pl-9" />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 flex-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="all">All statuses</SelectItem>
                </SelectContent>
              </Select>
              <Select value={severityFilter} onValueChange={setSeverityFilter}>
                <SelectTrigger className="h-8 flex-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All urgency</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="max-h-[700px] overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : filtered.length === 0 ? (
              <div className="px-6 py-16 text-center">
                <p className="text-sm font-medium">No cases here</p>
                <p className="mt-1 text-xs text-muted-foreground">New AI handoffs and cases will appear automatically.</p>
              </div>
            ) : filtered.map((row) => {
              const overdue = !["resolved", "closed"].includes(row.status) &&
                ((!row.acknowledged_at && new Date(row.acknowledgement_due_at).getTime() < Date.now()) || new Date(row.resolution_due_at).getTime() < Date.now());
              return (
                <button key={row.id} type="button" onClick={() => selectCase(row.id)} className={cn(
                  "w-full border-b border-border p-3 text-left transition-colors hover:bg-muted/50",
                  selectedId === row.id && "bg-muted/70",
                )}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{row.title}</p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">{row.reference} · {row.customer_label || "No customer linked"}</p>
                    </div>
                    <Badge variant="outline" className={cn("shrink-0 text-[10px] capitalize", severityClass[row.severity])}>{row.severity}</Badge>
                  </div>
                  <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{row.summary}</p>
                  <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>{ROLE_LABELS[row.assigned_role ?? ""] ?? "Unassigned"}</span>
                    <span className={overdue ? "font-medium text-orange-600" : ""}>{overdue ? "Deadline missed" : STATUS_LABELS[row.status]}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {!selected ? (
          <div className="hidden items-center justify-center px-8 text-center lg:flex">
            <div>
              <p className="text-sm font-medium">Select a case</p>
              <p className="mt-1 max-w-sm text-xs text-muted-foreground">Open a case to see ownership, deadlines, internal notes, files and the complete audit trail.</p>
            </div>
          </div>
        ) : (
          <div className="min-w-0">
            <div className="border-b border-border p-4">
              <Button variant="ghost" size="sm" className="mb-2 -ml-2 lg:hidden" onClick={() => selectCase(null)}>← Back to cases</Button>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className={cn("capitalize", severityClass[selected.severity])}>{selected.severity}</Badge>
                    <Badge variant="secondary">{CATEGORY_LABELS[selected.category] ?? selected.category}</Badge>
                    <span className="text-xs text-muted-foreground">{selected.reference}</span>
                  </div>
                  <h3 className="mt-2 text-lg font-semibold">{selected.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{selected.summary}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {!selected.acknowledged_at && !["resolved", "closed"].includes(selected.status) && (
                    <Button size="sm" disabled={busy === "ack"} onClick={() => act("ack", "acknowledge", {}, "Case acknowledged.")}>Acknowledge</Button>
                  )}
                  {!["resolved", "closed"].includes(selected.status) && (
                    <>
                      <Button variant="outline" size="sm" disabled={busy === "work"} onClick={() => act("work", "set_status", { status: "in_progress" }, "Case marked in progress.")}>Start work</Button>
                      <Button variant="outline" size="sm" onClick={() => { setCustomerOutcome(""); setResolveOpen(true); }}>Resolve</Button>
                    </>
                  )}
                  {["resolved", "closed"].includes(selected.status) && (
                    <Button variant="outline" size="sm" disabled={busy === "reopen"} onClick={() => act("reopen", "reopen", {}, "Case reopened.")}>Reopen</Button>
                  )}
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-lg border border-border p-3"><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Customer</p><p className="mt-1 truncate text-sm font-medium">{selected.customer_label || "Not linked"}</p></div>
                <div className="rounded-lg border border-border p-3"><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Status</p><p className="mt-1 text-sm font-medium">{STATUS_LABELS[selected.status]}</p></div>
                <div className="rounded-lg border border-border p-3"><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Acknowledge</p><p className={cn("mt-1 text-sm font-medium", !selected.acknowledged_at && new Date(selected.acknowledgement_due_at).getTime() < Date.now() && "text-orange-600")}>{selected.acknowledged_at ? fmt(selected.acknowledged_at) : relativeDeadline(selected.acknowledgement_due_at)}</p></div>
                <div className="rounded-lg border border-border p-3"><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Resolve</p><p className={cn("mt-1 text-sm font-medium", !["resolved", "closed"].includes(selected.status) && new Date(selected.resolution_due_at).getTime() < Date.now() && "text-orange-600")}>{selected.resolved_at ? fmt(selected.resolved_at) : relativeDeadline(selected.resolution_due_at)}</p></div>
              </div>
              {selected.outreach_pause_recommended && !["resolved", "closed"].includes(selected.status) && (
                <div className="mt-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <PauseCircle className="h-4 w-4 shrink-0" /> Proactive sales and follow-up messages are paused while people handle this case. Necessary booking messages still work.
                </div>
              )}
            </div>

            <div className="grid gap-5 p-4 xl:grid-cols-[1fr_300px]">
              <div className="space-y-4">
                {complaint && (
                  <div className="rounded-lg border border-border p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div><h4 className="text-sm font-semibold">Original complaint</h4><p className="mt-1 text-[11px] text-muted-foreground">{complaint.source.replace(/_/g, " ")} · {complaint.rating ? `${complaint.rating} / 5` : "No rating"} · {fmt(complaint.received_at)}</p></div>
                      <Badge variant={complaint.identity_status === "manual" || complaint.identity_status === "matched" ? "secondary" : "outline"}>{complaint.identity_status === "manual" || complaint.identity_status === "matched" ? "Customer verified" : "Identity needs checking"}</Badge>
                    </div>
                    {complaint.subject && <p className="mt-3 text-sm font-medium">{complaint.subject}</p>}
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{complaint.body}</p>
                    {(complaint.public_response || complaint.private_response) && <div className="mt-3 rounded-lg bg-muted/50 p-3"><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Approved response</p><p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed">{complaint.public_response || complaint.private_response}</p></div>}

                    {!isStaff && <div className="mt-4 space-y-3 border-t pt-4">
                      {complaint.identity_status !== "manual" && complaint.identity_status !== "matched" ? <>
                        <p className="text-xs leading-relaxed text-muted-foreground">Google display names are never matched automatically. Search for the real customer and verify the match yourself before proposing a credit.</p>
                        <Input value={linkContactSearch} onChange={(event) => setLinkContactSearch(event.target.value)} placeholder="Search customer by email, phone or name…" className="h-9" />
                        {linkContacts.length > 0 && <div className="rounded-lg border">{linkContacts.map((contact) => <button key={contact.contact_id} type="button" disabled={busy === "link-contact"} onClick={() => linkComplaintContact(contact)} className="flex w-full items-center justify-between gap-3 border-b px-3 py-2 text-left text-xs last:border-b-0 hover:bg-muted"><span className="font-medium">{contactLabel(contact)}</span><span className="text-muted-foreground">Verify & link</span></button>)}</div>}
                        {complaint.recovery_token_expires_at && new Date(complaint.recovery_token_expires_at).getTime() > Date.now() && <Button variant="outline" size="sm" className="gap-1.5" disabled={busy === "recovery-link"} onClick={copyRecoveryLink}><Clipboard className="h-3.5 w-3.5" /> Copy private identity link</Button>}
                      </> : <p className="text-xs text-muted-foreground">Linked to {complaint.customer_name || complaint.customer_email || "a verified customer"}{complaint.customer_email ? ` · ${complaint.customer_email}` : " · no email on file"}.</p>}

                      {recoveryOffer ? <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-muted/50 p-3"><div><p className="text-xs font-medium">{recoveryOffer.label}</p><p className="text-[11px] text-muted-foreground">{recoveryOffer.offer_type === "percentage_discount" ? `${Number(recoveryOffer.value)}% off` : `${recoveryOffer.currency} ${Number(recoveryOffer.value).toFixed(2)} credit`} · {recoveryOffer.status}{recoveryOffer.status === "issued" ? ` · code ends ${recoveryOffer.code_hint}` : ""}</p></div><Badge variant="outline">{recoveryOffer.status}</Badge></div> : <Button size="sm" disabled={busy === "propose-offer" || !["manual", "matched"].includes(complaint.identity_status) || !complaint.customer_email} onClick={proposeOffer}>Request recovery offer approval</Button>}
                      <Button variant="link" size="sm" className="h-auto px-0 text-xs" onClick={() => { const next = new URLSearchParams(searchParams); next.set("tab", "action-center"); next.delete("case"); setSearchParams(next); }}>Open approval queue →</Button>
                    </div>}
                  </div>
                )}
                <div>
                  <h4 className="text-sm font-semibold">Case timeline</h4>
                  <div className="mt-3 space-y-3 border-l border-border pl-4">
                    {events.map((event) => (
                      <div key={event.id} className="relative">
                        <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full border-2 border-background bg-muted-foreground" />
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-xs font-medium capitalize">{event.event_type.replace(/_/g, " ")}</p>
                          <span className="text-[10px] text-muted-foreground">{fmt(event.created_at)} · {event.actor_type === "human" ? "Team member" : event.actor_type}</span>
                        </div>
                        {event.body && <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">{event.body}</p>}
                      </div>
                    ))}
                  </div>
                </div>

                {!["resolved", "closed"].includes(selected.status) && (
                  <div className="rounded-lg border border-border p-3">
                    <Label className="text-xs">Internal note</Label>
                    <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="What did you check, decide, or tell the customer? Internal only." rows={3} className="mt-2 resize-none" />
                    <div className="mt-2 flex justify-end"><Button size="sm" variant="outline" className="gap-1.5" disabled={!comment.trim() || busy === "comment"} onClick={addComment}><MessageSquarePlus className="h-3.5 w-3.5" /> Add note</Button></div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border border-border p-3">
                  <h4 className="text-sm font-semibold">Ownership</h4>
                  <div className="mt-3 space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Responsible role</Label>
                      <Select disabled={isStaff} value={selected.assigned_role ?? "unassigned"} onValueChange={(value) => act("assign-role", "assign", { assigned_role: value === "unassigned" ? null : value, assigned_user_id: selected.assigned_user_id }, "Responsible role updated.") }>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {Object.entries(ROLE_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Specific person</Label>
                      <Select disabled={isStaff} value={selected.assigned_user_id ?? "unassigned"} onValueChange={(value) => act("assign-user", "assign", { assigned_role: selected.assigned_role, assigned_user_id: value === "unassigned" ? null : value }, "Assignee updated.") }>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">No specific person</SelectItem>
                          {staff.map((member) => <SelectItem key={member.user_id} value={member.user_id}>{member.display_name || member.email}</SelectItem>)}
                          {user?.id && !staff.some((member) => member.user_id === user.id) && <SelectItem value={user.id}>You</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-border p-3">
                  <div className="flex items-center justify-between">
                    <div><h4 className="text-sm font-semibold">Private files</h4><p className="text-[10px] text-muted-foreground">Evidence and internal documents · 10MB max</p></div>
                    <Button variant="ghost" size="sm" className="gap-1" onClick={() => fileInput.current?.click()} disabled={busy === "file"}><Paperclip className="h-3.5 w-3.5" /> Attach</Button>
                    <input ref={fileInput} type="file" className="hidden" onChange={uploadFile} />
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {files.length === 0 ? <p className="text-xs text-muted-foreground">No files attached.</p> : files.map((file) => (
                      <button key={file.id} onClick={() => openFile(file)} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-muted">
                        <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate text-xs">{file.file_name}</span>
                        <span className="text-[10px] text-muted-foreground">{Math.ceil(file.size_bytes / 1024)}KB</span>
                      </button>
                    ))}
                  </div>
                </div>

                {selected.resolution_summary && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                    <h4 className="text-sm font-semibold text-emerald-800">Recorded outcome</h4>
                    <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-emerald-800">{selected.resolution_summary}</p>
                    <p className="mt-2 text-xs font-medium text-emerald-900">
                      Customer outcome: {selected.customer_outcome === "recovered"
                        ? "Recovery confirmed"
                        : selected.customer_outcome === "not_recovered"
                          ? "Not recovered"
                          : "Not yet known"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Open a case</DialogTitle>
            <DialogDescription>Create a customer case or an operational incident. Keep the summary factual; do not add a diagnosis.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Customer (optional for an operational incident)</Label>
              {pickedContact ? (
                <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm"><span>{contactLabel(pickedContact)}</span><button className="text-xs text-muted-foreground" onClick={() => setPickedContact(null)}>Change</button></div>
              ) : (
                <>
                  <Input value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} placeholder="Search name, email or phone…" />
                  {contacts.length > 0 && <div className="max-h-36 overflow-y-auto rounded-lg border border-border">{contacts.map((contact) => <button key={contact.contact_id} className="block w-full border-b border-border px-3 py-2 text-left text-sm last:border-0 hover:bg-muted" onClick={() => setPickedContact(contact)}>{contactLabel(contact)}</button>)}</div>}
                </>
              )}
            </div>
            <div className="space-y-1.5"><Label>Factual summary</Label><Textarea value={form.summary} onChange={(e) => setForm((current) => ({ ...current, summary: e.target.value }))} rows={4} placeholder="What happened, what the customer reported, and what the team needs to do next…" /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Type</Label><Select value={form.case_type} onValueChange={(value) => setForm((current) => ({ ...current, case_type: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="customer_case">Customer case</SelectItem><SelectItem value="operational_incident">Operational incident</SelectItem></SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Category</Label><Select value={form.category} onValueChange={(value) => setForm((current) => ({ ...current, category: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(CATEGORY_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Urgency</Label><Select value={form.severity} onValueChange={(value) => setForm((current) => ({ ...current, severity: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="emergency">Emergency</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Responsible role</Label><Select value={form.assigned_role} onValueChange={(value) => setForm((current) => ({ ...current, assigned_role: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(ROLE_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5"><div><p className="text-sm font-medium">Pause proactive outreach</p><p className="text-xs text-muted-foreground">Recommended for complaints, clinical/safety/privacy concerns and customers asking for space.</p></div><Switch checked={form.pause_outreach} onCheckedChange={(value) => setForm((current) => ({ ...current, pause_outreach: value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button><Button onClick={createCase} disabled={busy === "create"}>{busy === "create" ? "Opening…" : "Open case"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Resolve {selected?.reference}</DialogTitle><DialogDescription>Record the actual human outcome. This clears only the case-owned outreach pause; it does not restart the AI conversation or promise the customer anything.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Customer outcome</Label>
              <Select value={customerOutcome} onValueChange={setCustomerOutcome}>
                <SelectTrigger><SelectValue placeholder="Choose the honest outcome" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="recovered">Recovery confirmed</SelectItem>
                  <SelectItem value="not_recovered">Not recovered</SelectItem>
                  <SelectItem value="unknown">Not yet known</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Closing internal work does not automatically mean the relationship was recovered.</p>
            </div>
            <div className="space-y-1.5"><Label>What was done?</Label><Textarea value={resolution} onChange={(e) => setResolution(e.target.value)} rows={5} placeholder="Who responded, what action was taken, and what follow-up was agreed…" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setResolveOpen(false)}>Cancel</Button><Button onClick={resolveCase} disabled={busy === "resolve"}>{busy === "resolve" ? "Resolving…" : "Resolve case"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
